/**
 * Reads the invitation mails that ship with a job campaign
 * (`NN_Name_Email_Einladung_Fragebogen2.md`).
 *
 * All 29 files in the 2026 handover share one shape, so the parser expects it
 * and says so when a file deviates rather than guessing:
 *
 *   # Einladungs-Mail · Fragebogen 2 · <Beiname>
 *   **Verwendung:** ...internal note, not part of the mail...
 *   ---
 *   **Betreff:** Deine Bewerbung: der nächste Schritt
 *
 *   Hallo {Vorname},
 *   ...body...
 */

const SUBJECT_LINE = /^\s*\*\*Betreff:\*\*\s*(.+?)\s*$/m;
const SEPARATOR = /^---\s*$/m;

/** The only placeholder the handover files use. */
export const PLACEHOLDER_PATTERN = /\{([A-Za-zÄÖÜäöüß]+)\}/g;

export function parseInvitationEmail(markdown) {
  const text = String(markdown || '');
  if (!text.trim()) {
    return { ok: false, error: 'Die Datei ist leer.' };
  }

  const subjectMatch = text.match(SUBJECT_LINE);
  if (!subjectMatch) {
    return { ok: false, error: 'Keine Zeile "**Betreff:**" gefunden.' };
  }

  // Everything after the separator is the mail. Without one, fall back to
  // everything after the subject line, which still drops the internal header.
  const separatorIndex = text.search(SEPARATOR);
  const afterSeparator =
    separatorIndex === -1 ? text : text.slice(separatorIndex).replace(SEPARATOR, '');

  const subjectIndexInBody = afterSeparator.search(SUBJECT_LINE);
  const body = afterSeparator
    .slice(subjectIndexInBody)
    .replace(SUBJECT_LINE, '')
    .trim();

  if (!body) {
    return { ok: false, error: 'Nach der Betreffzeile steht kein Text.' };
  }

  return {
    ok: true,
    subject: subjectMatch[1],
    body,
    placeholders: Array.from(new Set(Array.from(body.matchAll(PLACEHOLDER_PATTERN), (m) => m[1]))),
  };
}

/** Where the mail starts in a handover file, without swallowing the blank line above. */
const SUBJECT_START = /^[ \t]*\*\*Betreff:\*\*/m;

/**
 * The invitation mail as a handover file again, for the download.
 *
 * Subject and body come from what is stored now, so edits made in the
 * dashboard are in it. The header above them (title, usage note) is not
 * editable here and comes from the uploaded file. Without one, a minimal
 * header is written that the parser above still reads.
 */
export function composeInvitationMarkdown({ original, subject, body, title }) {
  const mail = `**Betreff:** ${subject || ''}\n\n${body || ''}\n`;
  const start = original ? original.search(SUBJECT_START) : -1;
  if (start !== -1) return original.slice(0, start) + mail;
  return `# Einladungs-Mail · Fragebogen 2${title ? ` · ${title}` : ''}\n\n---\n\n${mail}`;
}

/**
 * First name for the {Vorname} placeholder.
 *
 * Applicants are stored under a single `name`, so the first whitespace-separated
 * part is the best available guess. It is right for "Anna Müller" and for
 * "Anna-Lena Müller-Schmidt", and wrong for a leading title, which is why the
 * send dialog shows the filled-in text before anything goes out.
 */
export function firstNameOf(fullName) {
  const trimmed = String(fullName || '').trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0];
}

/** Substitute placeholders. Unknown ones are left visible rather than blanked. */
export function fillPlaceholders(text, values) {
  return String(text || '').replace(PLACEHOLDER_PATTERN, (match, key) => {
    const value = values?.[key];
    return value === undefined || value === null || value === '' ? match : String(value);
  });
}

/**
 * Marks where the personal questionnaire link belongs in the mail text.
 *
 * The digit and hyphen keep it intentionally outside PLACEHOLDER_PATTERN:
 * fillPlaceholders therefore leaves it alone, and it cannot be confused
 * with {Vorname}.
 */
export const LINK_PLACEHOLDER = '{Fragebogen2-URL}';

/**
 * Marks where the job advert's own page belongs.
 *
 * Not every job has one, so this placeholder can also be *removed*: see
 * composeInvitationBody. A label followed by nothing would read worse than
 * no mention at all.
 */
export const JOB_AD_PLACEHOLDER = '{Stellenanzeige-URL}';

/** Introduces the auto-inserted link so it doesn't stand alone. */
const LINK_INTRO = "Hier geht's zum Fragebogen:";

/**
 * Where the link goes when the text carries no placeholder.
 *
 * The link belongs after the greeting and the first body paragraph, to sit
 * where readers will see it. A two-paragraph mail gets it after the first
 * paragraph; a mail with three or more gets it as the third. A mail without
 * blank lines keeps it at the end, because there is nowhere else to put it.
 * Previously it stood at the very end under the sign-off and was overlooked.
 */
function insertLinkBlock(text, link) {
  const block = `${LINK_INTRO}\n${link}`;
  const paragraphs = text.split(/\n\s*\n/);
  if (paragraphs.length === 1) return `${text}\n\n${block}`;
  const at = paragraphs.length >= 3 ? 2 : 1;
  return [...paragraphs.slice(0, at), block, ...paragraphs.slice(at)].join('\n\n');
}

/**
 * The finished mail: placeholders filled, the personal link in its place.
 */
/**
 * Drops the whole paragraph a placeholder sits in.
 *
 * The templates introduce the job advert with a label on the line above it.
 * Replacing the placeholder with nothing would leave "Die Stellenanzeige:"
 * pointing at empty space, so the label goes with it.
 */
function dropParagraphWith(text, placeholder) {
  return text
    .split(/\n\s*\n/)
    .filter((paragraph) => !paragraph.includes(placeholder))
    .join('\n\n');
}

export function composeInvitationBody({ body, link, firstName, campaignUrl }) {
  let filled = fillPlaceholders(body, { Vorname: firstName });

  filled = campaignUrl
    ? filled.split(JOB_AD_PLACEHOLDER).join(campaignUrl)
    : dropParagraphWith(filled, JOB_AD_PLACEHOLDER);

  if (!link) return filled;
  if (filled.includes(LINK_PLACEHOLDER)) return filled.split(LINK_PLACEHOLDER).join(link);
  return insertLinkBlock(filled, link);
}

/**
 * Outlook for Windows passes a mailto: URL through the Windows shell, which
 * truncates it at roughly this length. Other clients allow far more.
 */
export const MAILTO_SAFE_LENGTH = 2048;

/**
 * A mailto: URL, plus whether it is long enough that some clients will cut it.
 *
 * The caller shows that warning rather than hiding the button: mailto opens a
 * draft, so a truncated text is visible before anything is sent, and on the
 * clients that handle long URLs it is the fastest path there is.
 */
export function buildMailtoUrl({ to, subject, body }) {
  const url =
    `mailto:${encodeURIComponent(to || '')}` +
    `?subject=${encodeURIComponent(subject || '')}` +
    `&body=${encodeURIComponent(body || '')}`;
  return { url, length: url.length, mayBeTruncated: url.length > MAILTO_SAFE_LENGTH };
}

/**
 * True when the filled text still carries an unresolved placeholder.
 *
 * Builds its own regex on purpose: `test()` on a shared /g pattern carries
 * `lastIndex` between calls and would answer differently every other time.
 */
export function hasUnfilledPlaceholders(text) {
  return new RegExp(PLACEHOLDER_PATTERN.source).test(String(text || ''));
}

/**
 * Does the finished mail still hold an unfilled placeholder?
 *
 * For the send dialog preview: nobody should accidentally send a {Vorname}.
 * Checks both kinds — the generic placeholder pattern and the link
 * placeholder, which the pattern does not recognize.
 */
export function hasOpenPlaceholders(text) {
  const value = String(text || '');
  return (
    hasUnfilledPlaceholders(text) ||
    value.includes(LINK_PLACEHOLDER) ||
    value.includes(JOB_AD_PLACEHOLDER)
  );
}

/**
 * The text split into runs, flagging the placeholders that are still in it.
 *
 * The preview highlights these: a warning that something is unfilled is only
 * useful if the reader can see *where*.
 */
export function splitOnPlaceholders(text) {
  const braced = [LINK_PLACEHOLDER, JOB_AD_PLACEHOLDER]
    .map((name) => name.replace(/[{}]/g, '\\$&'))
    .join('|');
  const pattern = new RegExp(`${PLACEHOLDER_PATTERN.source}|${braced}`, 'g');
  const parts = [];
  let last = 0;
  for (const match of String(text || '').matchAll(pattern)) {
    if (match.index > last) parts.push({ text: String(text).slice(last, match.index), placeholder: false });
    parts.push({ text: match[0], placeholder: true });
    last = match.index + match[0].length;
  }
  if (last < String(text || '').length) parts.push({ text: String(text).slice(last), placeholder: false });
  return parts;
}

/**
 * A conservative match for the one URL that matters in these mails: the
 * questionnaire link. Deliberately doesn't match bare `www.` or e-mail
 * addresses — over-matching would turn parts of the body into broken links.
 */
const URL_PATTERN = /https?:\/\/\S+/g;

/** Sentence punctuation that ends up right after a URL, not inside it. */
const TRAILING_PUNCTUATION = /[.,)]$/;

/**
 * `splitOnPlaceholders`, with the non-placeholder runs split further on
 * URLs, so the preview can render the questionnaire link as a clickable
 * anchor. Placeholder runs pass through untouched — a `{Vorname}` must never
 * become part of a link.
 *
 * A URL match's trailing `.`, `,` or `)` is moved back into the following
 * text run: "https://x.de/?t=1." must not turn the sentence-ending period
 * into part of the href. This can never clip a real character off a link
 * this app builds — `generateAccessToken` (URL-safe nanoid, alphabet
 * A-Za-z0-9_-) can't itself end in one of those three characters, and the
 * token is always the last part of the URL.
 */
export function splitOnPlaceholdersAndLinks(text) {
  const runs = [];
  for (const part of splitOnPlaceholders(text)) {
    if (part.placeholder) {
      runs.push({ ...part, link: false });
      continue;
    }
    let last = 0;
    for (const match of part.text.matchAll(URL_PATTERN)) {
      if (match.index > last) {
        runs.push({ text: part.text.slice(last, match.index), placeholder: false, link: false });
      }
      let url = match[0];
      if (TRAILING_PUNCTUATION.test(url)) url = url.slice(0, -1);
      runs.push({ text: url, placeholder: false, link: true });
      last = match.index + url.length;
    }
    if (last < part.text.length) {
      runs.push({ text: part.text.slice(last), placeholder: false, link: false });
    }
  }
  return runs;
}
